// Tiny async key-value store (browserdav-nephele-parity Phase 3).
//
// IndexedDB in the browser so locks and dead properties survive page reload;
// a process-wide in-memory registry when indexedDB is unavailable (Node
// conformance runs) — module-global so a "reload" (a second WebDAVFileSystem
// in the same process) sees the same data, mimicking IndexedDB persistence.
// Deliberately dependency-free like the rest of webdav/.

const memoryRegistry = new Map() // dbName/storeName → Map

function memoryStore(key) {
    if (!memoryRegistry.has(key)) memoryRegistry.set(key, new Map())
    const map = memoryRegistry.get(key)
    return {
        async get(k) {
            return map.has(k) ? map.get(k) : undefined
        },
        async set(k, value) {
            map.set(k, value)
        },
        async remove(k) {
            map.delete(k)
        },
        async entries() {
            return [...map.entries()]
        },
    }
}

function idbStore(dbName, storeName) {
    let dbPromise = null

    const openAt = (version) =>
        new Promise((resolve, reject) => {
            const req = version === undefined ? indexedDB.open(dbName) : indexedDB.open(dbName, version)
            req.onupgradeneeded = () => {
                if (!req.result.objectStoreNames.contains(storeName)) {
                    req.result.createObjectStore(storeName)
                }
            }
            req.onsuccess = () => resolve(req.result)
            req.onerror = () => reject(req.error)
        })

    // The DB is shared by stores opened from independent connections (locks,
    // dead-properties), so this store can be missing even when the DB exists:
    // whichever store opened first created the DB with only its own store, and
    // a same-version open never fires onupgradeneeded. If the store is missing,
    // bump the version to trigger an upgrade that adds it. Every connection
    // closes itself on versionchange so another store's bump is never blocked;
    // the next operation reopens.
    const open = () => {
        if (!dbPromise) {
            dbPromise = (async () => {
                for (;;) {
                    let db = await openAt(undefined)
                    if (!db.objectStoreNames.contains(storeName)) {
                        const bumped = db.version + 1
                        db.close()
                        try {
                            db = await openAt(bumped)
                        } catch (err) {
                            if (err && err.name === 'VersionError') continue // raced another bump — re-check
                            throw err
                        }
                    }
                    db.onversionchange = () => {
                        db.close()
                        dbPromise = null
                    }
                    return db
                }
            })()
            dbPromise.catch(() => {
                dbPromise = null
            })
        }
        return dbPromise
    }

    // Run an operation, retrying once if the connection was closed under us by
    // another store's version bump. Failures are rethrown as plain Errors so
    // IDB DOMException names (NotFoundError, ...) can't be mistaken for
    // filesystem errors by callers.
    const withDb = async (op, fn) => {
        let err
        try {
            return await fn(await open())
        } catch (e) {
            err = e
        }
        if (err && err.name === 'InvalidStateError') {
            dbPromise = null
            try {
                return await fn(await open())
            } catch (e) {
                err = e
            }
        }
        throw new Error(`kv store ${dbName}/${storeName} ${op}: ${(err && err.message) || err}`)
    }

    const tx = (db, mode, fn) =>
        new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, mode)
            const store = transaction.objectStore(storeName)
            const result = fn(store)
            transaction.oncomplete = () => resolve(result.__value !== undefined ? result.__value : undefined)
            transaction.onerror = () => reject(transaction.error)
        })

    return {
        async get(k) {
            return withDb('get', (db) =>
                new Promise((resolve, reject) => {
                    const req = db.transaction(storeName).objectStore(storeName).get(k)
                    req.onsuccess = () => resolve(req.result)
                    req.onerror = () => reject(req.error)
                })
            )
        },
        async set(k, value) {
            await withDb('set', (db) => tx(db, 'readwrite', (store) => ({ __req: store.put(value, k) })))
        },
        async remove(k) {
            await withDb('remove', (db) => tx(db, 'readwrite', (store) => ({ __req: store.delete(k) })))
        },
        async entries() {
            return withDb('entries', (db) =>
                new Promise((resolve, reject) => {
                    const store = db.transaction(storeName).objectStore(storeName)
                    const keysReq = store.getAllKeys()
                    const valuesReq = store.getAll()
                    let keys, values
                    const done = () => {
                        if (keys && values) resolve(keys.map((k, i) => [k, values[i]]))
                    }
                    keysReq.onsuccess = () => {
                        keys = keysReq.result
                        done()
                    }
                    valuesReq.onsuccess = () => {
                        values = valuesReq.result
                        done()
                    }
                    keysReq.onerror = () => reject(keysReq.error)
                    valuesReq.onerror = () => reject(valuesReq.error)
                })
            )
        },
    }
}

export function createKVStore(dbName, storeName) {
    if (typeof indexedDB === 'undefined') return memoryStore(`${dbName}/${storeName}`)
    return idbStore(dbName, storeName)
}
